import * as Sentry from '@sentry/react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import i18n from 'i18n';

import { AuthService } from 'services/AuthService';
import UsersService from 'services/UsersServices';
import { Services } from 'services';

import { parseJwt, transformErrorResponse } from 'utils';
import { getUserInfoFromSession } from 'utils/auth';

import { actionMapForOtherLoaders } from 'constants/index';
import { PermissionNames } from 'constants/PermissionNames';

const DIRECT_LOGIN_KEY = 'workerPortalDirectLogin';

const getAppAccess = (permissions: { id: string; name: string; source: string }[]) => {
  const access = {
    hasAdminModeAccess: false,
    hasWorkerModeAccess: false,
  };
  access.hasAdminModeAccess = permissions?.some(
    permission => permission.name === PermissionNames.AppAccess.WorkerPortalAdmin,
  );
  access.hasWorkerModeAccess = permissions?.some(
    permission => permission.name === PermissionNames.AppAccess.WorkerPortalWorker,
  );

  return access;
};

const authenticateUser = createAsyncThunk(
  actionMapForOtherLoaders.authenticateUser,
  async (query: { username?: string; password?: string }, thunkApi) => {
    const store = thunkApi.getState() as any;

    try {
      // await AuthService.configureCognitoForWorker();
      const cognitoUserName =
        query.username ||
        (store.onboarding.inviteMethod === 2 ? store.onboarding.mobilePhoneNumber : store.onboarding.email);
      if (query.password) {
        await AuthService.signIn({
          username: cognitoUserName,
          password: query.password,
        });
      }
      const session = (await AuthService.getSession()).getIdToken().payload;
      const userInfo = getUserInfoFromSession(session);

      const isDirectLogin = !!query.password;

      // Block pure admin direct login — admins must login via Freedom Web
      if (isDirectLogin && userInfo.isAdmin && !userInfo.isWorker) {
        await AuthService.signOut();
        return thunkApi.rejectWithValue({
          code: 'ADMIN_DIRECT_LOGIN_BLOCKED',
          message: i18n.t('Auth.AdminDirectLoginBlocked'),
          freedomWebUrl: process.env.REACT_APP_FREEDOM_WEB_URL,
        });
      }

      const response2 = await UsersService.getUserAvailablePermissions(userInfo.companyUserId);

      const { hasAdminModeAccess, hasWorkerModeAccess } = getAppAccess(response2?.data);

      // Determine login origin and compute isAdminSession
      let isAdminSession = false;
      if (isDirectLogin) {
        // Direct login — track in localStorage so page refresh preserves this
        localStorage.setItem(DIRECT_LOGIN_KEY, userInfo.userId);
      } else {
        // Session restore (no password) — check if this is a resumed direct-login or Freedom Web session
        const savedDirectLoginUserId = localStorage.getItem(DIRECT_LOGIN_KEY);
        if (savedDirectLoginUserId === userInfo.userId) {
          // Resumed direct-login session — keep as worker
          isAdminSession = false;
        } else if (userInfo.isAdmin) {
          // Admin session restored from Freedom Web cookies
          isAdminSession = true;
        }
      }

      // Override role flags based on effective session type
      if (isAdminSession) {
        // Admin+Worker via Freedom Web → treat as admin
        userInfo.isWorker = false;
      } else if (isDirectLogin && userInfo.isAdmin && userInfo.isWorker) {
        // Admin+Worker direct login → treat as worker, strip admin flags
        userInfo.isAdmin = false;
        userInfo.isSuperAdmin = false;
      }

      const requiredPermission = userInfo.isWorker ? hasWorkerModeAccess : hasAdminModeAccess;

      if (!requiredPermission) {
        await AuthService.signOut();
        return thunkApi.rejectWithValue({
          message: i18n.t('Auth.NoPermission'),
        });
      }

      userInfo.permissions = response2?.data || [];
      Sentry.setUser({
        id: userInfo.userId || userInfo.companyUserId,
        username: cognitoUserName,
        email: userInfo.email,
        phone: userInfo.mobilePhoneNumber,
      });
      return { ...userInfo, isAdminSession, username: cognitoUserName };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const authenticateUserInAdminSession = createAsyncThunk(
  actionMapForOtherLoaders.authenticateUserInAdminSession,
  async (query: { username?: string; password?: string }, thunkApi) => {
    try {
      const loginResponse = await UsersService.authenticate({
        username: query.username,
        password: query.password,
      });

      const session = parseJwt(loginResponse.data.sessionTokens.idToken);
      const userInfo = getUserInfoFromSession(session);

      if (!userInfo.isWorker && userInfo.isAdmin) {
        return thunkApi.rejectWithValue({
          code: 'ADMIN_DIRECT_LOGIN_BLOCKED',
          message: i18n.t('Auth.AdminDirectLoginBlocked'),
          freedomWebUrl: process.env.REACT_APP_FREEDOM_WEB_URL,
        });
      }

      const permissionsResponse = await UsersService.getAvailablePermissionsByUserId(userInfo.userId);

      const { hasWorkerModeAccess } = getAppAccess(permissionsResponse?.data);

      if (!hasWorkerModeAccess || !userInfo.isWorker) {
        return thunkApi.rejectWithValue({
          message: i18n.t('Auth.NoPermission'),
        });
      }

      Sentry.setUser({
        id: userInfo?.userId || userInfo?.companyUserId,
        username: loginResponse?.data?.username,
        email: userInfo?.email,
        phone: userInfo?.mobilePhoneNumber,
      });
      return { username: loginResponse.data?.username, ...userInfo };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

//* sessionExpired is required so every call site must declare whether the token is
//* still alive. Pass true from any path reached because auth already failed — the
//* token is dead there and DELETE /users/self/session would 401.
const logoutUser = createAsyncThunk(
  actionMapForOtherLoaders.logoutUser,
  async (params: { sessionExpired: boolean }, thunkApi) => {
    try {
      localStorage.removeItem(DIRECT_LOGIN_KEY);
      if (!params.sessionExpired) {
        //* Best effort — must run before signOut (needs the token) and must not
        //* reject, or the outer catch would skip signOut and leave the user logged in.
        await UsersService.deleteSession().catch(() => {});
      }
      await AuthService.signOut();
      Sentry.setUser(null);
      return true;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const resetPassword = createAsyncThunk(
  actionMapForOtherLoaders.resetPassword,
  async (data: { email?: string; mobilePhoneNumber?: string }, thunkApi) => {
    try {
      const response = await UsersService.resetPassword(data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const confirmResetPassword = createAsyncThunk(
  actionMapForOtherLoaders.confirmResetPassword,
  async (data: { email?: string; mobilePhoneNumber?: string; token: string; password: string }, thunkApi) => {
    try {
      const response = await UsersService.confirmResetPassword(data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const validateResetPasswordToken = createAsyncThunk(
  actionMapForOtherLoaders.validateResetPasswordToken,
  async (data: { email?: string; mobilePhoneNumber?: string; token: string }, thunkApi) => {
    try {
      const response = await UsersService.validateResetPasswordToken(data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

/**
 * Sends a one-time access PIN to the worker's registered email or mobile number
 * (`POST users/sendAccessPin`). Used by the Reset Password screen in Admin Mode (CH-4506).
 *
 * The API takes a single channel, so pass either `email` or `mobilePhoneNumber` — the same
 * one must then be passed to `verifyOtp`.
 */
const sendOtp = createAsyncThunk(
  actionMapForOtherLoaders.sendOtp,
  async (data: { email?: string; mobilePhoneNumber?: string; languageCode?: string }, thunkApi) => {
    try {
      const payload: { email?: string; phoneNumber?: string } = data.mobilePhoneNumber
        ? { phoneNumber: data.mobilePhoneNumber }
        : { email: data.email };

      const response = await Services.sendAccessPin(payload, data.languageCode);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

/**
 * Verifies the one-time access PIN entered by the worker (`POST users/ValidateAccessPin`, CH-4506).
 *
 * Resolves to `{ userId, workerId }`, which identifies the worker to load into the onboarding
 * slice — no worker session or token is created, the admin's session continues to authorize
 * every subsequent API call. A 200 without a `workerId` means the PIN was not accepted.
 */
const verifyOtp = createAsyncThunk(
  actionMapForOtherLoaders.verifyOtp,
  async (data: { pinCode: string; email?: string; mobilePhoneNumber?: string; languageCode?: string }, thunkApi) => {
    try {
      const payload = {
        ...(data.mobilePhoneNumber ? { phoneNumber: data.mobilePhoneNumber } : { email: data.email }),
        pinCode: data.pinCode?.toUpperCase(),
      };

      const response = await Services.validateAccessPin(payload, data.languageCode);

      if (!response?.data?.workerId) {
        return thunkApi.rejectWithValue({ message: i18n.t('verifyOtp.errors.invalidOtp') });
      }

      return response.data as { userId: string; workerId: string };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const getUserCompanies = createAsyncThunk(
  actionMapForOtherLoaders.getUserCompanies,
  async (userId: string, thunkApi) => {
    try {
      const response = await UsersService.getUserCompanies(userId);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const initiateauth = createAsyncThunk(
  actionMapForOtherLoaders.initiateauth,
  async (data: { username: string; clientId: string; customauthchallenge: string }, thunkApi) => {
    try {
      const response = await UsersService.initiateAuth(data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const respondToAuthChallenge = createAsyncThunk(
  actionMapForOtherLoaders.respondToAuthChallenge,
  async (
    data: { username: string; clientId: string; SelectedCompany: string; session: string; customauthchallenge: string },
    thunkApi,
  ) => {
    try {
      const response = await UsersService.respondToAuthChallenge(data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const changePassword = createAsyncThunk(
  actionMapForOtherLoaders.changePassword,
  async (
    data: { oldPassword: string; newPassword: string; isWorkerChangingPasswordInAdminSession?: boolean },
    thunkApi,
  ) => {
    try {
      if (data.isWorkerChangingPasswordInAdminSession) {
        const store = thunkApi.getState() as any;
        const userId = store.onboarding.userId;
        await UsersService.changePassword(userId, {
          currentPassword: data.oldPassword,
          newPassword: data.newPassword,
          languageCode: store.app.languageCode,
        });
        return true;
      }
      await AuthService.changePassword(data.oldPassword, data.newPassword);
      return true;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

export {
  authenticateUser,
  authenticateUserInAdminSession,
  logoutUser,
  resetPassword,
  confirmResetPassword,
  validateResetPasswordToken,
  sendOtp,
  verifyOtp,
  getUserCompanies,
  initiateauth,
  respondToAuthChallenge,
  changePassword,
};
